import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import Sketch from '@uiw/react-color-sketch';
import '../../styles/inputs/squareColorInput.scss';

interface Props {
  id: string;
  value: string;
  setValue: (input: string) => void;
  title?: string;
  disableAlpha?: boolean;
}

type MenuPos = {
  top: number;
  left: number;
  width: number;
  placement: 'bottom' | 'top';
};

const GAP = 8; // space between trigger and picker
const MIN_MARGIN = 8; // clamp inside viewport

const SquareColorInput = ({
  id,
  value,
  setValue,
  title,
  disableAlpha = true,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // ---- helpers -------------------------------------------------------------

  const clampLeft = (left: number, width: number) => {
    const maxLeft = window.scrollX + window.innerWidth - width - MIN_MARGIN;
    const minLeft = window.scrollX + MIN_MARGIN;
    return Math.max(minLeft, Math.min(left, maxLeft));
  };

  /** Compute base position using trigger rect before we know picker height */
  const computeBasePosition = useCallback((): MenuPos | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();

    // Sketch has a known min width (~220px), but we’ll still measure later.
    const widthGuess = Math.max(rect.width, 220);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const heightGuess = 300; // rough guess to prefer below when possible

    const placement: 'bottom' | 'top' =
      spaceBelow >= heightGuess || spaceBelow >= spaceAbove ? 'bottom' : 'top';

    // Align right edge of picker to right edge of trigger (prevents right cutoff)
    const guessedLeft = rect.right + window.scrollX - widthGuess;

    const baseTop =
      placement === 'bottom'
        ? rect.bottom + window.scrollY + GAP
        : rect.top + window.scrollY - GAP;

    return {
      top: Math.max(window.scrollY + MIN_MARGIN, baseTop),
      left: clampLeft(guessedLeft, widthGuess),
      width: Math.max(rect.width, 220),
      placement,
    };
  }, []);

  /** Refine with actual picker size; flip if needed and clamp to viewport */
  const refineWithMeasuredHeight = useCallback(() => {
    if (!menuRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    let placement: 'bottom' | 'top' =
      spaceBelow >= menuRect.height || spaceBelow >= spaceAbove
        ? 'bottom'
        : 'top';

    // align right edge of picker to right edge of trigger
    const leftRaw = triggerRect.right + window.scrollX - menuRect.width;
    const left = clampLeft(leftRaw, menuRect.width);

    const top =
      placement === 'bottom'
        ? triggerRect.bottom + window.scrollY + GAP
        : triggerRect.top + window.scrollY - menuRect.height - GAP;

    setMenuPos((prev) =>
      prev
        ? {
            ...prev,
            top: Math.max(window.scrollY + MIN_MARGIN, top),
            left,
            placement,
          }
        : {
            top: Math.max(window.scrollY + MIN_MARGIN, top),
            left,
            width: Math.max(triggerRect.width, menuRect.width),
            placement,
          }
    );
  }, []);

  // ---- open/close ----------------------------------------------------------

  const openMenu = useCallback(() => {
    setOpen(true);
    const base = computeBasePosition();
    setMenuPos(base);
  }, [computeBasePosition]);

  const closeMenu = useCallback(() => setOpen(false), []);

  // ---- effects: outside click, esc, reposition on scroll/resize ------------

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inTrigger = !!triggerRef.current && triggerRef.current.contains(t);
      const inMenu = !!menuRef.current && menuRef.current.contains(t);
      if (!inTrigger && !inMenu) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, closeMenu]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeMenu]);

  useLayoutEffect(() => {
    if (!open) return;

    const onScrollOrResize = () => {
      const base = computeBasePosition();
      setMenuPos(base);
      // refine after menu paint
      requestAnimationFrame(refineWithMeasuredHeight);
    };

    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, computeBasePosition, refineWithMeasuredHeight]);

  // after the portal mounts, measure its true size and refine placement
  useLayoutEffect(() => {
    if (!open) return;
    requestAnimationFrame(refineWithMeasuredHeight);
  }, [open, refineWithMeasuredHeight]);

  // ---- render --------------------------------------------------------------

  return (
    <div
      id={id}
      className='squareColorInput'
      ref={rootRef}
      title={title || 'Select color'}
    >
      <div
        ref={triggerRef}
        className='squareColorInput--colorView'
        style={{ backgroundColor: value }}
        onClick={() => (open ? closeMenu() : openMenu())}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open ? closeMenu() : openMenu();
          }
        }}
        aria-label='Open color picker'
        aria-haspopup='dialog'
        aria-expanded={open}
      />

      {/* Portal picker */}
      {open &&
        menuPos &&
        createPortal(
          <div
            ref={(el) => (menuRef.current = el)}
            style={{
              position: 'absolute',
              top: menuPos.top,
              left: menuPos.left,
              minWidth: menuPos.width,
              maxWidth: 'calc(100vw - 16px)',
              zIndex: 10000,
            }}
            // Make sure clicks inside don’t bubble to outside-click handler
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Sketch color={value} onChange={(c) => setValue(c.hex)} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default SquareColorInput;
